import { useState, useEffect, useCallback } from 'react';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { useIsTestUser } from '../../../hooks/useIsTestUser';
import { Card, CardHeader, CardContent } from '@mui/material';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { Provider } from '../../../data/models/provider.model';
import * as providerSlice from '../../../data/provider.slice';
import * as referenceSlice from '../../../data/reference.slice';
import ElementDialog, { formClasses } from '../ElementDialog';
import providerUtils from '../../../services/providerUtils';
import BasicInfoForm from './BasicInfoForm';
import BillingInfoForm from './BillingInfoForm';
import LegalInfoForm from './LegalInfoForm';
import TransportationInfoForm from './TransportationInfoForm';
import InsuranceInfoForm from './InsuranceInfoForm';
import documentUtils from '../../../services/documentUtils';
import { useIsInternalUser } from '../../../hooks/useIsInternalUser';
import { DateTime } from 'luxon';

interface ProviderDialogProps {
  title: string;
  open: boolean;
  provider?: Provider;
  onboardingProviderName?: string;
  close: (saved: boolean) => void;
  onboarding?: boolean;
}

export default function ProviderDialog({
  title,
  open,
  provider,
  onboardingProviderName,
  close,
  onboarding = false,
}: ProviderDialogProps) {
  const { t } = useTranslation('common');
  const requiredFieldMessage = t('common.requiredField');
  const dispatch = useAppDispatch();
  const isInternalUser = useIsInternalUser();
  const isTestUser = useIsTestUser();

  const [hasDocuments, setHasDocuments] = useState(false);
  const [hasAutoInsurance, setHasAutoInsurance] = useState(false);

  const legalEntityStatuses = useAppSelector(
    referenceSlice.selectLegalEntityStatuses,
  );
  const legalEntityTypes = useAppSelector(
    referenceSlice.selectLegalEntityTypes,
  );
  const transportationTypes = useAppSelector(
    referenceSlice.selectTransortationTypes,
  );
  const stateCodes = useAppSelector(referenceSlice.selectStateCodes);
  const insuranceStrengths = useAppSelector(
    referenceSlice.selectInsuranceStrengths,
  );
  const messagingMethods = useAppSelector(
    referenceSlice.selectMessagingMethods,
  );
  const documentTypes = useAppSelector(
    referenceSlice.selectProviderDocumentTypes,
  );
  const documents = useAppSelector(providerSlice.selectProviderDocuments);

  const saving = useAppSelector(providerSlice.selectSavingProvider);
  const saved = useAppSelector(providerSlice.selectSavedProvider);
  const savingError = useAppSelector(providerSlice.selectSavingError);

  const {
    control,
    formState,
    handleSubmit,
    reset,
    getValues,
    setValue,
    watch,
  } = useForm({
    mode: 'onTouched',
  });

  const insuranceVal = watch('AutoInsuranceExpiresOn');
  const isCTTChecked = watch('IsClearToTransport');

  const saveProvider = useCallback(
    (data: any) => {
      const id =
        provider && !onboarding ? provider.transportationProviderID : undefined;
      const p = providerUtils.getProviderFromFormData(
        isTestUser,
        id,
        data,
        provider?.emailAddresses,
        provider?.phoneNumbers,
        provider?.addresses,
        provider?.insurancePolicies,
        provider?.transportationProviderCoverageAreas
          ?.map((tpca) => tpca.coverageArea)
          .filter(Boolean) as any,
        provider?.isInternal,
        transportationTypes,
      );
      const fn = id
        ? providerSlice.updateProviderAsync(p)
        : providerSlice.createProviderAsync(p);
      dispatch(fn);
    },
    [dispatch, provider, isTestUser],
  );

  // This is here so after a save, the updated provider is used to populate the form values
  useEffect(() => {
    const legalEntityNATypeID = legalEntityTypes.filter(
      (type) => type.legalEntityTypeBC === 'NA',
    )[0]?.legalEntityTypeID;

    const legalEntityLLCTypeID = legalEntityTypes.filter(
      (type) => type.legalEntityTypeBC === 'LLC',
    )[0]?.legalEntityTypeID;
    reset(
      providerUtils.getDefaults(
        provider,
        onboardingProviderName,
        legalEntityNATypeID,
        legalEntityLLCTypeID,
      ),
    );
  }, [provider, reset]);

  useEffect(() => {
    if (documentTypes && documents) {
      setHasDocuments(
        documentUtils.hasAllRequiredDocuments(documentTypes, documents),
      );
    }
  }, [documentTypes, documents]);

  useEffect(() => {
    if (isCTTChecked) {
      setValue('IsReadyClearToTransport', false);
    }
  }, [isCTTChecked, setValue]);

  useEffect(() => {
    if (insuranceVal && insuranceVal.isValid) {
      const now = DateTime.now().startOf('day');
      setHasAutoInsurance(insuranceVal > now);
    } else {
      setHasAutoInsurance(false);
    }
  }, [insuranceVal]);

  useEffect(() => {
    if (provider?.transportationProviderID) {
      dispatch(
        providerSlice.fetchProviderDocumentsAsync(
          provider.transportationProviderID,
        ),
      );
    } else {
      dispatch(providerSlice.setProviderDocuments([]));
    }
  }, [provider, dispatch]);

  return (
    <ElementDialog
      title={title}
      faqContext="providers_details_form"
      open={open}
      saving={saving}
      saved={saved}
      savingError={savingError}
      save={handleSubmit(saveProvider)}
      reset={reset}
      close={close}
      formState={formState}>
      <Card className={formClasses.card}>
        <CardHeader title={t('common.contactInfo')} />
        <CardContent className={formClasses.cardContent}>
          <BasicInfoForm
            control={control}
            formState={formState}
            requiredFieldMessage={requiredFieldMessage}
            stateCodes={stateCodes}
            messagingMethods={messagingMethods}
            getValues={getValues}
            setValue={setValue}
          />
        </CardContent>
      </Card>
      <Card className={formClasses.card}>
        <CardHeader title={t('provider.billing')} />
        <CardContent className={formClasses.cardContent}>
          <BillingInfoForm
            control={control}
            formState={formState}
            requiredFieldMessage={requiredFieldMessage}
            stateCodes={stateCodes}
            getValues={getValues}
            setValue={setValue}
          />
        </CardContent>
      </Card>
      <Card className={formClasses.card}>
        <CardHeader title={t('provider.legal')} />
        <CardContent className={formClasses.cardContent}>
          <LegalInfoForm
            control={control}
            formState={formState}
            requiredFieldMessage={requiredFieldMessage}
            stateCodes={stateCodes}
            legalEntityTypes={legalEntityTypes}
            legalEntityStatuses={legalEntityStatuses}
            getValues={getValues}
            setValue={setValue}
          />
        </CardContent>
      </Card>
      <Card className={formClasses.card}>
        <CardHeader title={t('provider.transport')} />
        <CardContent className={formClasses.cardContent}>
          <TransportationInfoForm
            control={control}
            formState={formState}
            requiredFieldMessage={requiredFieldMessage}
            transportationTypes={transportationTypes}
            hasRequiredDocuments={hasDocuments}
            hasValidAutoInsurance={hasAutoInsurance}
            isCTTChecked={isCTTChecked}
            isInternalUser={onboarding || isInternalUser}
            isNewProvider={!provider}
          />
        </CardContent>
      </Card>
      <Card className={formClasses.card}>
        <CardHeader title={t('provider.insurance')} />
        <CardContent className={formClasses.cardContent}>
          <InsuranceInfoForm
            control={control}
            formState={formState}
            watch={watch}
            requiredFieldMessage={requiredFieldMessage}
            insuranceStrengths={insuranceStrengths}
          />
        </CardContent>
      </Card>
    </ElementDialog>
  );
}
